<template>
  <!-- Drop-in replacement for Buefy's b-tooltip (registered under the same
       name): on small screens the tooltip is appended to the body, flips
       to the side with the most room (top / bottom) and is shifted back
       inside the viewport when it would run off the screen. -->
  <b-tooltip-base
    ref="tip"
    v-bind="computedProps"
    v-on="$listeners"
    @open="onOpen"
    @close="onClose"
  >
    <slot />
    <template
      v-if="$slots.content"
      #content
    >
      <slot name="content" />
    </template>
  </b-tooltip-base>
</template>

<script>
import BTooltip from "buefy/src/components/tooltip/Tooltip.vue";

const MOBILE_MAX = 768;
const EDGE = 8;
// Visible width: innerWidth can be wider than the screen on phones when
// something overflows (the appended bubbles themselves, for one)
const viewportWidth = () => document.documentElement.clientWidth || window.innerWidth;
// Room a side bubble needs (multilined medium = 250px + arrow)
const SIDE_ROOM = 270;

export default {
  components: {
    "b-tooltip-base": BTooltip,
  },
  inheritAttrs: false,
  props: {
    position: {
      type: String,
      default: "is-top",
    },
    appendToBody: {
      type: Boolean,
      default: false,
    },
    multilined: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      smallScreen: typeof window !== "undefined" && viewportWidth() <= MOBILE_MAX,
      // Position actually used (recomputed before every opening)
      side: this.position,
    };
  },
  computed: {
    computedProps() {
      const props = {
        ...this.$attrs,
        position: this.side,
        appendToBody: this.appendToBody,
        multilined: this.multilined,
      };

      if (this.smallScreen) {
        props.appendToBody = true;
        props.multilined = true;
      }

      return props;
    },
  },
  watch: {
    position(value) {
      this.side = value;
    },
  },
  mounted() {
    // Decide the side before Buefy positions the bubble (mouseover and
    // touchstart fire before the mouseenter that opens it)
    this.$el.addEventListener("mouseover", this.pickSide, true);
    this.$el.addEventListener("touchstart", this.pickSide, { capture: true, passive: true });
    this.$el.addEventListener("focusin", this.pickSide, true);
    window.addEventListener("resize", this.onResize);
  },
  beforeDestroy() {
    this.$el.removeEventListener("mouseover", this.pickSide, true);
    this.$el.removeEventListener("touchstart", this.pickSide, { capture: true });
    this.$el.removeEventListener("focusin", this.pickSide, true);
    window.removeEventListener("resize", this.onResize);
  },
  methods: {
    onResize() {
      this.smallScreen = viewportWidth() <= MOBILE_MAX;
    },
    // The element the bubble is anchored to (Buefy's trigger span), not
    // the wrapper, which can be as wide as its table cell
    triggerRect() {
      const tip = this.$refs.tip;
      const trigger = (tip && tip.$refs && tip.$refs.trigger) || this.$el;

      return trigger.getBoundingClientRect();
    },
    pickSide() {
      const rect = this.triggerRect();
      const viewport = viewportWidth();
      let side = this.position;

      if (this.smallScreen) {
        // Phones: always above or below, whichever has room
        side = rect.top > 150 ? "is-top" : "is-bottom";
      } else if (side === "is-right" && rect.right + SIDE_ROOM > viewport) {
        side = rect.left - SIDE_ROOM > 0 ? "is-left" : "is-top";
      } else if (side === "is-left" && rect.left - SIDE_ROOM < 0) {
        side = rect.right + SIDE_ROOM < viewport ? "is-right" : "is-top";
      }

      this.side = side;
    },
    bubble() {
      const tip = this.$refs.tip;

      if (tip && tip.$data._bodyEl) {
        const wrapper = tip.$data._bodyEl.children[0];

        return wrapper ? wrapper.querySelector(".tooltip-content") : null;
      }

      return this.$el.querySelector(".tooltip-content");
    },
    // Above / below bubbles are centred on the trigger: slide them back
    // inside the viewport when the trigger sits near an edge. The bubble
    // gets its final width a few frames after opening, so retry briefly.
    onOpen() {
      if (this.side !== "is-top" && this.side !== "is-bottom") {
        return;
      }
      const apply = () => {
        const content = this.bubble();

        if (!content) {
          return;
        }
        const width = content.getBoundingClientRect().width;

        if (!width) {
          return;
        }
        const trigger = this.triggerRect();
        const center = trigger.left + trigger.width / 2;
        const viewport = viewportWidth();
        let shift = 0;

        if (center - width / 2 < EDGE) {
          shift = EDGE - (center - width / 2);
        } else if (center + width / 2 > viewport - EDGE) {
          shift = viewport - EDGE - (center + width / 2);
        }

        content.style.transform = shift
          ? `translateX(calc(-50% + ${Math.round(shift)}px))`
          : "";
      };

      [30, 150, 400].forEach((delay) => setTimeout(apply, delay));
    },
    onClose() {
      const content = this.bubble();

      if (content) {
        content.style.transform = "";
      }
    },
  },
};
</script>

<style>
/* Small screens: bubbles never wider than the viewport */
@media screen and (max-width: 768px) {
  .b-tooltip .tooltip-content {
    max-width: calc(100vw - 16px) !important;
    white-space: normal;
  }
}
</style>
